import { Template, LazyTemplate } from "../../core/template/index";
import { View } from "../../core/view";
import { JobEntry, JobTaskEntry, JobTaskEntryDetail } from "../../app/datatypes";
import OrderItemFlipcardView from "../orderItemFlipcard/index";
import AppHeader from "../../core/app/header";
import Order, { OrderLike } from "../../app/db/models/order";
import { ValtasiirtoBaseApp } from "../../app/valtaSiirtoBaseApp";
import * as ArrayHelpers from "../../core/helpers/array";
import OrderItem, { OrderItemLike } from "../../app/db/models/orderItem";
import TabControlView from "../../core/views/tabControl/index";
import { AutoSyncEndEvent, AutoSyncResult } from "../../core/synchronizer/index";
import OrderAssignment from "../../app/db/models/orderAssignment";

export default class OrderItemListView extends View
{
    public app: ValtasiirtoBaseApp;
    public template = Template.lazy('/views/orderItemList/index.html');

    private orders: OrderLike[];
    private orderItemViews: OrderItemFlipcardView[];
    private tabControl: TabControlView;

    public data: OrderItemListViewData;

    constructor(data?: OrderItemListViewData)
    {
        super(data);
        this.data.activeOrderItemCount = 0;
        this.data.completedOrderItemCount = 0;
        this.data.upcomingOrderItemCount = 0;
    }

    private resolveActiveTabPage()
    {
        if (this.data.activeTabPage != null)
            return this.data.activeTabPage;
        else if (this.data.activeOrderItemCount > 0)
            return OrderItem.STATE_STARTED;
        else if(this.data.upcomingOrderItemCount > 0)
            return OrderItem.STATE_NEW;
        else if(this.data.completedOrderItemCount > 0)
            return OrderItem.STATE_COMPLETED;
        else
            return OrderItem.STATE_NEW;
    }

    public beforeRender(): PromiseLike<void>
    {
        this.app.header.text = this.pgettext('Title - list of order items', 'Order items');
        this.orderItemViews = [];

        return this.app.db.OrderAssignment.where('user_id').equals(this.app.user.id).toArray().then((currentAssignments) =>
        {   
            // Select orders that are assigned to the current user
            return Order.withAll(this.app.db, this.app.db.Order.where('state').equals(Order.STATE_ASSIGNED).and(
                o => currentAssignments.find(a => a.order_id == o.id) != null
            ));
        }).then((orders) =>  
        {
            Order.sort(orders);
            
            for (const order of orders) {
                (order as any).priorityLabel = Order.priorityLabel(this, order);
                (order as any).priorityCssClassName = Order.priorityCssClassName(order);
            }
            this.orders = orders;
            
            let viewRenders: PromiseLike<any>[] = [];

            for(let order of this.orders)
            {
                let sortedItems = order.items.sort((a, b) => new Date(b.modified_at).getTime() - new Date(a.modified_at).getTime());
                for (let item of sortedItems)
                {
                    this.data.activeOrderItemCount += (item.state == OrderItem.STATE_STARTED ? 1 : 0);
                    this.data.upcomingOrderItemCount += (item.state == OrderItem.STATE_NEW ? 1 : 0);
                    this.data.completedOrderItemCount += (item.state == OrderItem.STATE_COMPLETED ? 1 : 0);
                    
                    let view = new OrderItemFlipcardView(item);
                    viewRenders.push(this.app.renderPartialView(view));
                    this.orderItemViews.push(view);
                }
            }

            this.tabControl = new TabControlView({
                activePage: this.resolveActiveTabPage(),
                tabPageClassNames: 'item-container order-items',
                pages: [
                    {title: this.pgettext('Title - upcoming order items', 'Upcoming'), id: OrderItem.STATE_NEW, itemCount: this.data.upcomingOrderItemCount},
                    {title: this.pgettext('Title - active order items', 'Active'), id: OrderItem.STATE_STARTED, itemCount: this.data.activeOrderItemCount},
                    {title: this.pgettext('Title - finished order items', 'Finished'), id: OrderItem.STATE_COMPLETED, itemCount: this.data.completedOrderItemCount}
                ]
            })

            viewRenders.push(this.app.renderPartialView(this.tabControl));
            return Promise.all(viewRenders);
        }).then(() => 
        {

        });
    }

    public afterRender(): PromiseLike<void>
    {
        for(let view of this.orderItemViews)
        {
            this.tabControl.getPage(view.data.state).appendChild(view.element);
        }

        this.appendChild(this.tabControl);
        this.app.synchronizer.on('autoSyncEnd', this.onAutoSyncEnd);

        return super.afterRender();
    }

    private onAutoSyncEnd = (e: AutoSyncEndEvent) =>
    {
        this.data.activeTabPage = this.tabControl.activePage;
        if (e.result === AutoSyncResult.CHANGED)
        {
            this.app.refreshCurrentView();
        }
    }

    public unload(): PromiseLike<void>
    {
        this.app.synchronizer.off('autoSyncEnd', this.onAutoSyncEnd);
        return super.unload();
    }
}

export interface OrderItemListViewData
{
    activeOrderItemCount: number;
    upcomingOrderItemCount: number;
    completedOrderItemCount: number;
    activeTabPage?: string;
}